<template>
  <div class="max-h-[calc(100vh-2rem)] overflow-y-auto rounded-md border bg-white">
    <div class="border-b px-4 py-5 sm:px-6">
      <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
        <div class="ml-2 mt-2">
          <h3 class="text-base font-medium leading-6 text-foreground">
            {{ $t(`pages.campaign.campaignCreatePage.${campaignType}.summary.title`) }}
          </h3>
        </div>
        <div v-if="step != 3" class="ml-4 mt-2 shrink-0">
          <Button
            variant="default-primary"
            class="fixed bottom-0 left-0 w-screen rounded-none py-4 sm:static sm:w-auto sm:rounded-control sm:py-2"
            :disabled="
              (step === 1 && optinTargets > 0 && campaignName?.length > 0) || (step === 2 && canProceedToValidationStep)
                ? false
                : true
            "
            @click="$emit('continue')"
            >{{ $t('pages.campaign.campaignCreatePage.buttons.continue') }}
          </Button>
        </div>
      </div>
    </div>

    <div v-if="campaignType === 'push'" class="px-4 my-6">
      <BaseTip severity="info" :text="$t('pages.campaign.campaignCreatePage.push.summary.tip')" />
    </div>

    <div class="padding-4 m-4 mb-6 flex items-center rounded-md bg-muted p-3">
      <span class="inline-flex rounded-lg p-2">
        <img class="w-8" src="/images/campaign/target.svg" alt="target" />
      </span>
      <div class="block">
        <p class="ml-4 text-sm text-foreground">
          <span v-if="isLoadingEstimation">{{
            $t('pages.campaign.campaignCreatePage.summary.recipientsLoading')
          }}</span>
          <span v-else>{{
            $t('pages.campaign.campaignCreatePage.summary.recipients', optinTargets > 1 ? 2 : 1, {
              count: optinTargets
            })
          }}</span>
        </p>
        <div v-if="isLoadingEstimation" class="ml-4 flex items-center text-sm">
          <svg
            class="mr-3 h-5 w-5 animate-spin text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span class="text-muted-foreground">
            {{ $t('pages.campaign.campaignCreatePage.summary.calculationProgress') }}
          </span>
        </div>
        <p v-else class="ml-4 text-xl font-bold text-foreground">
          {{ optinTargets.toLocaleString('fr') }}
        </p>
      </div>
    </div>
    <CampaignUpsell v-if="campaignType === 'sms'" class="m-4" />
    <dl class="px-6" :class="surveys.length > 0 ? '' : 'pb-5'">
      <div>
        <div>
          <dd class="mt-1 text-foreground">
            <div class="flex justify-between">
              <h3 class="text-sm font-medium">
                {{ $t('pages.campaign.campaignTargetBlock.categories.location') }}
              </h3>
              <span
                v-if="recipientsFilters.stores.length > 4"
                class="cursor-pointer text-sm text-foreground"
                @click="showMoreStores = !showMoreStores"
              >
                {{
                  showMoreStores
                    ? $t('pages.campaign.campaignTargetBlock.details.less')
                    : $t('pages.campaign.campaignTargetBlock.details.more')
                }}
                {{ $t('pages.campaign.campaignTargetBlock.details.title') }}
              </span>
            </div>
            <div
              v-if="activeRecipientsFilters.stores"
              class="mx-2 my-2 text-sm"
              :class="[
                recipientsFilters.stores.length > 8 ? 'max-h-16 overflow-hidden' : '',
                showMoreStores ? 'max-h-36 overflow-scroll' : ''
              ]"
            >
              <BaseChip
                v-if="!showMoreStores && recipientsFilters.stores.length > 4"
                class="mb-2 mr-2"
                :text="`${recipientsFilters.stores.length} ${$t('global.words.store', recipientsFilters.stores.length)}`"
              >
                <template #leading>
                  <Store />
                </template>
              </BaseChip>
              <BaseChip
                v-else-if="!showMoreStores && recipientsFilters.stores.length === 0"
                class="mb-2 mr-2"
                :text="$t('pages.campaign.campaignTargetBlock.select.store.all')"
              >
                <template #leading>
                  <Store />
                </template>
              </BaseChip>
              <span v-else-if="!recipientsFilters.stores.length">
                {{ $t('pages.campaign.campaignTargetBlock.noSelectedStores') }}
              </span>
              <div v-else class="flex flex-wrap">
                <BaseChip
                  v-for="store in recipientsFilters.stores"
                  :key="store"
                  class="mb-2 mr-2"
                  :text="store.label"
                />
              </div>
            </div>
            <div class="flex justify-between">
              <h3 class="text-sm font-medium">
                {{ $t('pages.campaign.campaignTargetBlock.categories.segments') }}
              </h3>
              <span
                v-if="recipientsFilters.lists.length > 4"
                class="cursor-pointer text-sm text-foreground"
                @click="showMoreSegments = !showMoreSegments"
              >
                {{
                  showMoreSegments
                    ? $t('pages.campaign.campaignTargetBlock.details.less')
                    : $t('pages.campaign.campaignTargetBlock.details.more')
                }}
                {{ $t('pages.campaign.campaignTargetBlock.details.title') }}
              </span>
            </div>
            <div
              v-if="activeRecipientsFilters.lists || activeRecipientsFilters.excludedLists"
              class="mx-2 my-2 text-sm"
            >
              <template v-if="recipientsFilters.lists.length">
                <h4 class="text-muted-foreground mb-1 text-xs">
                  {{ $t('pages.campaign.campaignTargetBlock.segments.included') }}
                </h4>
                <div class="flex flex-wrap">
                  <BaseChip
                    v-for="listId in recipientsFilters.lists"
                    :key="listId"
                    class="mb-2 mr-2"
                    :text="lists.find((list) => list.id === listId)?.name"
                  />
                </div>
              </template>
              <template v-if="recipientsFilters.excludedLists && recipientsFilters.excludedLists.length">
                <h4 class="text-muted-foreground mb-1 text-xs">
                  {{ $t('pages.campaign.campaignTargetBlock.segments.excluded') }}
                </h4>
                <div class="flex flex-wrap">
                  <BaseChip
                    v-for="listId in recipientsFilters.excludedLists"
                    :key="'excluded-' + listId"
                    class="mb-2 mr-2"
                    :text="lists.find((list) => list.id === listId)?.name"
                  />
                </div>
              </template>
              <span v-else-if="!recipientsFilters.lists.length">
                {{ $t('pages.campaign.campaignTargetBlock.segments.allCustomers.sms') }}
              </span>
            </div>

            <div class="mb-4">
              <h3 class="text-sm font-medium">
                {{ $t('pages.campaign.campaignTargetBlock.categories.profile') }}
              </h3>
              <div class="pl-2">
                <div v-if="activeRecipientsFilters.genders.length > 0" class="my-2 inline-flex items-center pr-2">
                  <BaseChip :text="activeRecipientsFilters.genders.map((f) => $t('global.gender.' + f)).join(' - ')" />
                </div>
                <BaseChip
                  class="mr-2"
                  :text="
                    activeRecipientsFilters.ages[0] === activeRecipientsFilters.ages[1]
                      ? $t('global.persona.age', { age: activeRecipientsFilters.ages[0] })
                      : $t('pages.campaign.campaignTargetBlock.agesFromTo', {
                          age1: activeRecipientsFilters.ages[0],
                          age2: activeRecipientsFilters.ages[1]
                        })
                  "
                />
                <BaseChip
                  :text="
                    activeRecipientsFilters.points[0] === activeRecipientsFilters.points[1]
                      ? $t('global.persona.points', { points: activeRecipientsFilters.points[0] })
                      : $t('pages.campaign.campaignTargetBlock.pointsFromTo', {
                          points1: activeRecipientsFilters.points[0],
                          points2: activeRecipientsFilters.points[1]
                        })
                  "
                />
                <BaseChip
                  :text="
                    $t('pages.campaign.campaignTargetBlock.checkinFromTo', {
                      checkin1: activeRecipientsFilters.checkins[0],
                      checkin2: activeRecipientsFilters.checkins[1]
                    })
                  "
                />
              </div>
            </div>

            <div>
              <h3 class="text-sm font-medium">
                {{ $t('pages.campaign.campaignTargetBlock.categories.commitment') }}
              </h3>
              <div>
                <div v-if="activeRecipientsFilters?.frequencyTypes?.length > 0" class="mt-2 rounded-sm p-1 px-2">
                  <h4 class="text-muted-foreground mb-1 text-xs">
                    {{ $t('pages.campaign.campaignTargetBlock.categories.types') }}
                  </h4>
                  <BaseChip
                    v-if="
                      activeRecipientsFilters.frequencyTypes.length === 4 ||
                      activeRecipientsFilters.frequencyTypes.length === 0
                    "
                    :text="$t('pages.campaign.campaignTargetBlock.allTypes')"
                  />
                  <BaseChip
                    v-for="frequency in activeRecipientsFilters.frequencyTypes"
                    v-else
                    :key="frequency"
                    class="mr-2"
                    :color="getFrequencyChipColor(frequency)"
                    :text="getFrequency(frequency, 'name')"
                  />
                </div>
                <div v-if="contestGames?.length > 0" class="mt-2 rounded-sm px-2">
                  <h4 class="text-muted-foreground mb-1 text-xs">
                    {{ $t('pages.campaign.campaignTargetBlock.categories.contestGames') }}
                  </h4>
                  <BaseChip
                    v-if="
                      activeRecipientsFilters.contestGameIds.length === 4 ||
                      activeRecipientsFilters.contestGameIds.length === 0
                    "
                    class="mb-2"
                    :text="$t('pages.campaign.campaignTargetBlock.allContestGames')"
                  />
                  <BaseChip
                    v-for="activeContestGame in activeRecipientsFilters.contestGameIds"
                    v-else
                    :key="activeContestGame"
                    class="mb-2 mr-2"
                    :text="contestGames.filter((t) => t.id == activeContestGame)[0].name"
                  />
                </div>
                <div class="mt-1 rounded-sm px-2">
                  <h4 class="text-muted-foreground mb-1 text-xs">
                    {{ $t('pages.campaign.campaignTargetBlock.categories.tags') }}
                  </h4>
                  <BaseChip
                    v-if="!activeRecipientsFilters.tags.length"
                    class="mb-2"
                    :text="$t('pages.campaign.campaignTargetBlock.allTags')"
                  />
                  <BaseChip
                    v-for="activeTag in activeRecipientsFilters.tags"
                    v-else
                    :key="activeTag"
                    class="mb-2 mr-2"
                    :color="getTagChipColor(activeTag)"
                    :text="tags.filter((t) => t.id == activeTag)[0].name"
                  />
                </div>
                <div class="rounded-sm p-1 px-2">
                  <h4 class="text-muted-foreground mb-1 text-xs">
                    {{ $t('pages.campaign.campaignTargetBlock.categories.dates') }}
                  </h4>
                  <BaseChip class="mb-2 mr-2 whitespace-normal" :text="lastVisitDate" />
                  <BaseChip class="mb-1 whitespace-normal" :text="firstCheckinDate" />
                </div>
                <div v-if="surveys.length" class="mt-2 h-20 overflow-y-auto rounded-sm p-1 px-2">
                  <h4 class="text-muted-foreground mb-1 text-xs">
                    {{ $t('pages.campaign.campaignTargetBlock.surveys') }}
                  </h4>
                  <BaseChip class="whitespace-normal" :text="choiceNames" />
                </div>
              </div>
            </div>
          </dd>
        </div>
      </div>
    </dl>
  </div>
</template>

<script>
import { Store } from 'lucide-vue-next'

import BaseTip from '@/components/base/BaseTip.vue'
import CampaignUpsell from './components/CampaignUpsell.vue'

import { Button } from '@/ui/button'
import { hexToChipColor } from '@/utils/chipColor'

export default {
  components: {
    BaseTip,
    Button,
    CampaignUpsell,
    Store
  },
  props: {
    campaignName: {
      type: String,
      required: true
    },
    recipientsFilters: {
      type: Object,
      required: true
    },
    frequencyTypes: {
      type: Object,
      default: () => ({})
    },
    optinTargets: {
      type: Number,
      required: true
    },
    step: {
      type: Number,
      required: true
    },
    surveys: {
      type: [Array, Boolean],
      required: true
    },
    tags: {
      type: Array,
      required: true
    },
    contestGames: {
      type: Array,
      default: () => []
    },
    isModalOpenedFromPricesBlock: {
      type: Boolean,
      required: false
    },
    lists: {
      type: Array,
      required: true
    },
    isLoadingEstimation: {
      type: Boolean,
      required: true
    },
    canProceedToValidationStep: {
      type: Boolean,
      required: true
    }
  },
  emits: ['continue'],

  setup() {
    const route = useRoute()

    const campaignType = computed(() => route.query.type || 'sms')

    return {
      campaignType
    }
  },

  data: () => {
    return {
      showMoreStores: false,
      showMoreSegments: false
    }
  },

  computed: {
    lastVisitDate() {
      return this.activeRecipientsFilters.lastVisit.from
        ? this.$t('pages.campaign.campaignTargetBlock.lastVisitFromTo', {
            lastVisit1: this.activeRecipientsFilters.lastVisit.from,
            lastVisit2: this.activeRecipientsFilters.lastVisit.to
          })
        : this.$t('pages.campaign.campaignTargetBlock.allVisits')
    },
    firstCheckinDate() {
      return this.activeRecipientsFilters.firstCheckin.from
        ? this.$t('pages.campaign.campaignTargetBlock.firstCheckinFromTo', {
            firstCheckin1: this.activeRecipientsFilters.firstCheckin.from,
            firstCheckin2: this.activeRecipientsFilters.firstCheckin.to
          })
        : this.$t('pages.campaign.campaignTargetBlock.allCheckins')
    },
    activeRecipientsFilters() {
      let filters = { ...this.recipientsFilters }
      if (this.surveys === false) {
        delete filters.choices
      }
      return filters
    },
    choiceNames() {
      if (!this.recipientsFilters.surveyQuestionChoices) {
        return
      }
      if (this.recipientsFilters.surveyQuestionChoices.length === 0) {
        return this.$t('pages.campaign.campaignTargetBlock.allSurveys')
      }

      const choiceNames = []
      this.recipientsFilters.surveyQuestionChoices.map((choiceId) =>
        this.surveys.filter((s) => {
          s.choices.map((choice) => {
            if (choice.id == choiceId) {
              choiceNames.push(choice.text)
            }
          })
        })
      )

      return choiceNames.join(' - ')
    }
  },
  methods: {
    getTagChipColor(tagId) {
      return hexToChipColor(this.tags.filter((t) => t.id == tagId)[0].color)
    },
    getFrequencyChipColor(frequency) {
      const color = this.getFrequency(frequency, 'bgColor')?.match(/^bg-([a-z]+)-/)?.[1]
      return color === 'stone' ? 'gray' : hexToChipColor(color)
    },
    getFrequency(id, value) {
      return this.frequencyTypes.filter((f) => f.id == id)[0][value]
    }
  }
}
</script>
